import api from '../../../api/client';
import React, { useEffect, useState } from "react";
import { Row, Col, Card, Table, Spinner, Tabs, Tab } from "react-bootstrap";
import DataTable from "react-data-table-component";
import { Line, Pie } from "react-chartjs-2";
import "chart.js/auto";
import config from "../../../config";
import MainCard from "../../../components/Card/MainCard";
import { formatKES } from "../../../utils/currency";

const SalesAnalytics = ({ analytics }) => {
  const [loading, setLoading] = useState(false);

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center p-5">
        <Spinner animation="border" variant="primary" />
      </div>
    );
  }

  if (!analytics) {
    return <p className="text-danger">No analytics data available.</p>;
  }

  const { metrics, breakdown, chartData } = analytics;

  // Pie chart for category breakdown
  const categories = breakdown?.byCategory
    ? Object.keys(breakdown?.byCategory)
    : [];
  const categoryTotals = breakdown?.byCategory
    ? Object.values(breakdown?.byCategory).map((c) => c?.total || 0)
    : [];

  const pieData = {
    labels: categories,
    datasets: [
      {
        data: categoryTotals,
        backgroundColor: [
          "#4e73df",
          "#1cc88a",
          "#36b9cc",
          "#f6c23e",
          "#e74a3b",
          "#c53be7ff",
        ],
      },
    ],
  };

  // Table columns
  const columns = [
    { name: "Category", selector: (row) => row?.category, sortable: true },
    {
      name: "Transactions",
      selector: (row) => row?.transactions,
      sortable: true,
    },
    {
      name: "Total",
      selector: (row) =>
        row?.total?.toLocaleString("en-KE", {
          style: "currency",
          currency: "KES",
        }),
      sortable: true,
    },
  ];

  const column = [
    {
      name: "Category",
      selector: (row) => row.category,
      sortable: true,
    },
    {
      name: "Date",
      selector: (row) => new Date(row.recorded).toLocaleDateString(),
      sortable: true,
    },
    {
      name: "Stakeholder",
      selector: (row) => row.stakeholderId || "N/A",
    },
    {
      name: "Total Cost",
      selector: (row) => `KES ${row?.totalCost?.toLocaleString()}`,
      sortable: true,
    },
    {
      name: "Paid Amount",
      selector: (row) => `KES ${row?.paidAmount?.toLocaleString()}`,
      sortable: true,
    },
    {
      name: "Balance",
      selector: (row) => `KES ${row?.balance?.toLocaleString()}`,
      sortable: true,
    },
    {
      name: "Status",
      cell: (row) => (
        <span
          className={`badge ${
            row.status === "Paid"
              ? "bg-success"
              : row.status === "Partially Paid"
              ? "bg-warning text-dark"
              : "bg-danger"
          }`}
        >
          {row.status}
        </span>
      ),
      sortable: true,
    },
  ];

  const tableData = Object.entries(breakdown?.byCategory).map(
    ([category, val]) => ({
      category,
      transactions: val.transactions,
      total: val.total,
    }),
  );

  return (
    <>
      {/* Top Metrics */}
      <Row className="mb-4">
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <Card.Title>Total Sales</Card.Title>
              <h5 className="text-primary">
                KES{" "}
                {metrics?.totalSales?.toLocaleString("en-KE", {
                  minimumFractionDigits: 2,
                })}
              </h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <Card.Title>Avg Daily Sales</Card.Title>
              <h5 className="text-success">
                KES{" "}
                {metrics?.avgDailySales?.toLocaleString("en-KE", {
                  minimumFractionDigits: 2,
                })}
              </h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <Card.Title>Total Transactions</Card.Title>
              <h5 className="text-warning">{metrics?.totalTransactions}</h5>
            </Card.Body>
          </Card>
        </Col>
        <Col md={3}>
          <Card className="shadow-sm text-center">
            <Card.Body>
              <Card.Title>Top Category</Card.Title>
              <h5 className="text-info">{metrics?.topCategory?.category}</h5>
              <small>
                KES{" "}
                {metrics?.topCategory?.total?.toLocaleString("en-KE", {
                  minimumFractionDigits: 2,
                })}
              </small>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Charts */}
      <Row>
        <Col md={8} className="mb-4">
          <MainCard title="Sales Trend">
            <Line data={chartData} />
          </MainCard>
        </Col>
        <Col md={4} className="mb-4">
          <MainCard title="Category Breakdown">
            <Pie data={pieData} />
          </MainCard>
        </Col>
        s
      </Row>

      {/* Table */}
      <Tabs defaultActiveKey="wean">
        <Tab eventKey="wean" title="RECENT SALES" className="mt-4">
          <Row>
            <Col>
              <MainCard title="Recent Sales">
                <DataTable
                  columns={column}
                  data={analytics?.transactions}
                  pagination
                  highlightOnHover
                  striped
                  responsive
                  bordered
                  paginationPerPage={20}
                  paginationRowsPerPageOptions={[10, 20, 30, 40, 50, 100]}
                  paginationComponentOptions={{
                    rowsPerPageText: "Rows per page:",
                  }}
                  paginationResetDefaultPage={true}
                  paginationTotalRows={analytics?.transactions?.length}
                  customStyles={{
                    headRow: {
                      style: {
                        backgroundColor: "#279e6c", // Green background
                        color: "#fff", // White text
                        textTransform: "uppercase", // Uppercase text
                        // fontSize: '10px', // Adjust font size for header row
                        minHeight: "1.5rem",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                      },
                    },
                  }}
                />
              </MainCard>
            </Col>
          </Row>
        </Tab>
        <Tab eventKey="weaner" title="SALES BY CATEGORY" className="mt-4">
          <Row>
            <Col>
              <MainCard title="Sales by Category">
                <DataTable
                  columns={columns}
                  data={tableData}
                  pagination
                  highlightOnHover
                  striped
                  bordered
                  paginationPerPage={20}
                  paginationRowsPerPageOptions={[10, 20, 30, 40, 50, 100]}
                  paginationComponentOptions={{
                    rowsPerPageText: "Rows per page:",
                  }}
                  paginationResetDefaultPage={true}
                  paginationTotalRows={tableData?.length}
                  customStyles={{
                    headRow: {
                      style: {
                        backgroundColor: "#279e6c", // Green background
                        color: "#fff", // White text
                        textTransform: "uppercase", // Uppercase text
                        // fontSize: '10px', // Adjust font size for header row
                        minHeight: "1.5rem",
                        paddingTop: "8px",
                        paddingBottom: "8px",
                      },
                    },
                  }}
                />
              </MainCard>
            </Col>
          </Row>
        </Tab>
      </Tabs>
    </>
  );
};

const MetricCard = ({ title, value, subValue, icon, color }) => (
  <Card className={`text-center border-${color}`}>
    <Card.Body>
      <div className={`text-${color} mb-2`}>
        <i className={`${icon} fa-2x`}></i>
      </div>
      <h5 className={`text-${color}`}>{formatKES(value) || 0}</h5>
      <p className="text-muted mb-0">{title}</p>
      {subValue && <small className="text-muted">{subValue}</small>}
    </Card.Body>
  </Card>
);

export default SalesAnalytics;
